/**
 * Keeps the member price badge in sync with the price currently on display.
 *
 * Liquid renders the badge from the variant price, which is correct until an app
 * raises the displayed price in the browser — a personalization upcharge for a
 * name or number, for example. Those apps rewrite the price element without
 * telling the theme, so the badge is recomputed from whatever the price element
 * actually reads.
 *
 * @module member-price
 */

import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const BADGE_SELECTOR = '.member-price-badge';
const AMOUNT_SELECTOR = '.member-price-badge__amount';

/**
 * Matches a price range such as "€299,00 - €349,00". Subtracting a flat discount
 * from a range is ambiguous, so those are left as Liquid rendered them.
 */
const RANGE_PATTERN = /\d\s*[-–—]\s*\D*\d/;

/** Guards against reacting to our own writes. */
let isWriting = false;

/**
 * Finds the visible price element that the badge belongs to.
 * @param {Element} badge - The badge element
 * @returns {Element|null} The price element, or null when none is visible
 */
function findPriceElement(badge) {
  // Cards rendered outside a `product-price` element fall back to the price
  // container, and then to the badge's own parent.
  const scope =
    badge.closest('product-price') ?? badge.closest('[ref="priceContainer"]') ?? badge.parentElement;
  if (!scope) return null;

  const candidates = scope.querySelectorAll('.price');

  for (const candidate of candidates) {
    // Skip the hidden half of the regular/sale pair, and anything inside a badge.
    if (candidate.closest('.price__hidden')) continue;
    if (candidate.closest(BADGE_SELECTOR)) continue;
    if (candidate.offsetParent === null) continue;
    return candidate;
  }

  return null;
}

/**
 * Recomputes one badge from the price currently on display.
 * @param {Element} badge - The badge element
 */
function updateBadge(badge) {
  const amountElement = badge.querySelector(AMOUNT_SELECTOR);
  if (!amountElement) return;

  const discount = Number(badge.dataset.memberDiscount);
  const format = badge.dataset.moneyFormat;
  const currency = badge.dataset.currency;
  if (!Number.isFinite(discount) || discount <= 0 || !format || !currency) return;

  const priceElement = findPriceElement(badge);
  if (!priceElement) return;

  const priceText = priceElement.textContent ?? '';
  if (RANGE_PATTERN.test(priceText)) return;

  const price = convertMoneyToMinorUnits(priceText, currency);
  if (price == null) return;

  const memberPrice = price - discount;

  // A discount that swallows the price would read as free or negative.
  if (memberPrice <= 0) {
    badge.hidden = true;
    return;
  }

  const formatted = formatMoney(memberPrice, format, currency);
  if (amountElement.textContent === formatted) {
    badge.hidden = false;
    return;
  }

  isWriting = true;
  amountElement.textContent = formatted;
  badge.hidden = false;
  isWriting = false;
}

/** Recomputes every badge on the page. */
function updateAll() {
  for (const badge of document.querySelectorAll(BADGE_SELECTOR)) {
    updateBadge(badge);
  }
}

let frame = null;

/** Coalesces bursts of DOM mutations into a single recompute. */
function scheduleUpdate() {
  if (isWriting || frame !== null) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    updateAll();
  });
}

const observer = new MutationObserver((records) => {
  if (isWriting) return;

  for (const record of records) {
    // Our own writes land inside a badge; everything else is worth reacting to.
    if (record.target instanceof Element && record.target.closest(BADGE_SELECTOR)) continue;
    scheduleUpdate();
    return;
  }
});

function start() {
  updateAll();
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
